import { z } from 'zod';
import { CalendarDate } from './project.js';
import { Estimate } from './estimate.js';
/**
 * Cân bằng lại theo tiến độ thực — APM-AI-z (`plan-ai-features.md` mục 14).
 *
 * Mỗi lần một việc xong sớm hoặc trễ quá ngưỡng, hệ thống dựng **vài phương
 * án** chứ không một lời khuyên. PM chọn; không có phương án nào tự áp dụng.
 *
 * ## Vì sao `options` chứ không `recommendation`
 *
 * Ba phương án cho một lần trễ là ba cách trả giá khác nhau: dời lịch (mất
 * ngày), chuyển người (mất bối cảnh, đụng cảm nhận công bằng), cắt phạm vi (mất
 * việc). Không có phép tính nào chọn hộ được, vì cái giá nằm ở chỗ hệ thống
 * không đo được. Đưa một lời khuyên duy nhất là giả vờ ngược lại.
 */
export const DriftCause = z.enum(['early', 'late']);
export const RebalanceTrigger = z.object({
    taskKey: z.string().min(1).max(40),
    cause: DriftCause,
    /** Hạn đã hứa (`baseline_due_date` nếu có). */
    planned: CalendarDate.nullable(),
    /** Ngày thật sự đóng, hoặc `null` khi việc còn mở mà đã quá hạn. */
    actual: CalendarDate.nullable(),
    deltaDays: z.number().int().min(0),
});
export const RebalanceMove = z.object({
    taskKey: z.string().min(1).max(40),
    from: CalendarDate.nullable(),
    /** `null` nghĩa là **gỡ khỏi lịch** (phương án cắt phạm vi), không phải xoá. */
    to: CalendarDate.nullable(),
    assigneeId: z.uuid().nullable(),
    assigneeName: z.string().max(200).nullable(),
    reason: z.string().min(1).max(300),
});
export const RebalanceOptionName = z.enum(['keep_people', 'share_load', 'cut_scope', 'pull_up']);
export const RebalanceOption = z.object({
    name: RebalanceOptionName,
    /** Tên phương án viết cho người đọc, tiếng Việt. */
    label: z.string().min(1).max(120),
    moves: z.array(RebalanceMove).max(20),
    /** Kế hoạch dịch bao nhiêu ngày nếu áp. Âm là kéo lên. */
    p50ShiftDays: z.number(),
    /** Ai bị đổi lịch hoặc đổi việc. Độ dài của mảng này **là** cái giá con người. */
    touchesPeople: z.array(z.uuid()).max(30),
    /** Theo hợp đồng ước lượng §0.4 — APM-AI-y. */
    estimate: Estimate,
});
export const RebalanceOnDriftOutput = z.object({
    trigger: RebalanceTrigger,
    impact: z.object({
        milestonesAtRisk: z.array(z.string().max(200)).max(10),
        /** Mã việc trên chuỗi phụ thuộc bị ảnh hưởng. */
        chain: z.array(z.string().max(40)).max(50),
    }),
    /**
     * Có thể **rỗng**, và đó là một câu trả lời đúng.
     *
     * Mọi phương án đều vướng năng lực, hoặc không có gì phía sau để sắp lại —
     * bắt buộc phải có ít nhất một phương án là mời model bịa một cái để có thứ
     * điền vào. Rỗng thì `note` nói vì sao.
     */
    options: z.array(RebalanceOption).max(4),
    /** Tên phương án khuyên chọn. Vẫn là gợi ý — PM bấm mới đổi gì. */
    recommended: RebalanceOptionName.nullable(),
    /** Vì sao không có phương án nào, khi `options` rỗng. */
    note: z.string().max(400).nullable(),
    rationale: z.string().min(1).max(800),
});
/**
 * Một việc đang lệch, kèm phương án đã tính — APM-AI-z (đường **một chạm**).
 *
 * Phương án ở đây do solver dựng, **không** qua model: trang này chạy khi tổ
 * chức chưa có key AI, và nó là đường làm tay mà `rebalance_on_drift` trỏ tới.
 */
export const DriftedTaskView = z.object({
    taskId: z.uuid(),
    taskKey: z.string().min(1).max(40),
    title: z.string().max(500),
    cause: DriftCause,
    deltaDays: z.number().int().min(0),
    planned: CalendarDate.nullable(),
    actual: CalendarDate.nullable(),
    /**
     * Deadline **hiện tại** (`due_date`) — thứ giao diện hiện và cho sửa nhanh.
     *
     * Từ 2026-09-23 `planned` cũng là deadline hiện tại (không còn là baseline),
     * nên "trễ N ngày" và chip hạn trên cùng một dòng luôn nói về cùng một mốc.
     * Bộ lọc chọn việc vào danh sách cũng đọc cái này, nên dời deadline sang sau
     * hôm nay là việc tự rời khỏi dải.
     */
    dueDate: CalendarDate.nullable(),
    options: z.array(RebalanceOption.omit({ estimate: true })).max(4),
});
export const DriftedTasksView = z.object({
    projectId: z.uuid(),
    tasks: z.array(DriftedTaskView).max(20),
});
/**
 * Áp một phương án — APM-AI-z.
 *
 * Thân chỉ mang **tên** phương án, không mang danh sách nước đi. Client gửi
 * nước đi nghĩa là client quyết định thay đổi gì, và giữa lúc thông báo hiện ra
 * với lúc người ta bấm thì lịch đã có thể đổi. Server tính lại từ đầu, kiểm
 * năng lực, rồi áp — hoặc từ chối kèm lý do đọc được.
 */
export const ApplyRebalanceInput = z.object({
    taskId: z.uuid(),
    option: RebalanceOptionName,
});
export const ApplyRebalanceResult = z.object({
    /** Bao nhiêu việc thật sự đổi lịch hoặc đổi người. */
    appliedCount: z.number().int().nonnegative(),
});
//# sourceMappingURL=ai-rebalance.js.map